import { DelayTimer, JotType, KeyboardEventKeyCode } from "consts";
import Modal, { ModalDirection } from "components/modal/Modal";
import React, { useEffect, useRef, useState } from "react";
import {
  handleNoShiftKeyCodeTriggers,
  handleShiftKeyCodeTriggers,
  isFramed,
  isNullOrUndefOrEmpty,
  jsonToUri,
  onFocusRef,
  widgetCompliance,
} from "utils";

import Callout from "components/callout/Callout";
import Compliance from "components/compliance/Compliance";
import { IconClose } from "components/icons/Icons";
import PropTypes from "prop-types";
import ShareExpandable from "components/sharing/ShareExpandable";
import { actions } from "actions";
import classNames from "classnames";
import { connect } from "unistore/preact";
import { envConfig } from "config";
import i18n from "micro-i18n";

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/menu/MenuModal.scss';

const MenuModal = ({
  id,
  hostname,
  shareableLink,
  backupAlias,
  targetId,
  complianceUrl,
  partnerName,
  privacyPolicyUrl,
  showModal,
  onClose,
  compliance,
  complianceState,
  onComplianceAgree,
  theme,
  closeFocusRef,
  focusable = false,
  jot,
}) => {
  if (!targetId && !complianceUrl) {
    return null;
  }
  const { protocol, widgetHistoryEndpoint, majorVersion, publisherUrl } = envConfig;
  const historyUrlBase = `${protocol}${hostname}${widgetHistoryEndpoint}`;
  const isNotFramed = !isFramed();

  const modalFocusRef = useRef(null);
  const optOutFocusRef = useRef(null);
  const optOutChoiceFocusRef = useRef(null);
  const closeButtonBottomRef = useRef(null);
  const [showComplianceOptOutChoices, setShowComplianceOptOutChoices] = useState(false);
  const showComplianceOptOut = widgetCompliance.complianceCheckOn(compliance);
  const needsComplianceAgreement = widgetCompliance.needsComplianceResponse(compliance, complianceState);

  /**
   * Calculate historyUrl based on compliance and alias state.
   * @returns {string|null} history url or null if `needsComplianceAgreement`
   */
  const getHistoryUrl = () =>
    needsComplianceAgreement
      ? null
      : `${historyUrlBase}?${jsonToUri({ mv: majorVersion, target: targetId, backupAlias })}`;

  /**
   * Calculate manageAnswersUrl based on compliance and alias state.
   * @returns {string|null} manager answers url or null if `needsComplianceAgreement`
   */
  const getManageAnswersUrl = () =>
    needsComplianceAgreement ? null : widgetCompliance.buildManageAnswersUrl(compliance, backupAlias);

  const historyUrl = getHistoryUrl();
  const manageAnswersUrl = getManageAnswersUrl();

  // useEffect for showComplianceOptOutChoices
  useEffect(() => {
    focusable && setTimeout(setOptOutFocus, DelayTimer.WidgetMenuFocusDelay);
  }, [showComplianceOptOutChoices]);

  const setOptOutFocus = () => {
    const nextOptOutFocusRef = showComplianceOptOutChoices ? optOutChoiceFocusRef : optOutFocusRef;
    focusable && onFocusRef(nextOptOutFocusRef);
  };

  const toggleShowComplianceOptOutChoices = () => {
    !showComplianceOptOutChoices && jot({ space: "poll", type: JotType.MenuOo });
    setShowComplianceOptOutChoices(!showComplianceOptOutChoices);
  };

  const onOptOutCancel = () => {
    jot({ space: "poll", type: JotType.MenuOoN });
    setShowComplianceOptOutChoices(false);
  };

  const onOptOut = () => {
    jot({ space: "poll", type: JotType.MenuOoY });
    setShowComplianceOptOutChoices(false);
    onComplianceAgree(false);
    onClose();
  };

  const onManageAnswers = () => {
    jot({ space: "poll", type: JotType.MenuHistory });
  };

  const onTabKey = (event, focusRef, checkShift = false) => {
    const shiftKeyHandler = checkShift ? handleShiftKeyCodeTriggers : handleNoShiftKeyCodeTriggers;

    return shiftKeyHandler(event, [KeyboardEventKeyCode.Tab], () => onFocusRef(focusRef));
  };

  return (
    <Modal
      id={id}
      className="csw-menu-modal"
      showModal={showModal}
      direction={ModalDirection.Right}
      onClose={onClose}
      theme={theme}
      openFocusRef={modalFocusRef}
      closeFocusRef={closeFocusRef}
      focusable={focusable}
      ariaLabeledById={`${id}-heading`}
    >
      <header ref={modalFocusRef} tabIndex="-1" onKeyDown={(e) => onTabKey(e, closeButtonBottomRef, true)}>
        <span role="heading" aria-level="1" id={`${id}-heading`}>
          {i18n.t("MENU_TITLE")}
        </span>
        <button
          type="button"
          className="csw-menu-btn csw-btn csw-btn-icon outline"
          onClick={onClose}
          aria-label={i18n.t("MENU_CLOSE")}
          title={i18n.t("MENU_CLOSE")}
        >
          <IconClose />
          <span className="sr-only">{i18n.t("MENU_CLOSE")}</span>
        </button>
      </header>

      <div className="csw-modal-scroll">
        <ul className="csw-modal-list">
          {!needsComplianceAgreement && manageAnswersUrl && (
            <li>
              <a
                className="csw-menu-link outline"
                href={manageAnswersUrl}
                onClick={onManageAnswers}
                target="_blank"
                rel="noopener noreferrer"
              >
                {i18n.t("COMPLIANCE_MANAGE_ANSWERS")}
              </a>
            </li>
          )}
          {!needsComplianceAgreement && historyUrl && (
            <li>
              <a className="csw-menu-link outline" href={historyUrl}>
                {i18n.t("DOWNLOAD_ANSWERS")}
              </a>
            </li>
          )}
          {!isNullOrUndefOrEmpty(privacyPolicyUrl) && (
            <li>
              <a className="csw-menu-link outline" href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                {partnerName
                  ? i18n.t("COMPLIANCE_PARTNER_PRIVACY_POLICY", { partnerName })
                  : i18n.t("COMPLIANCE_PUBLISHER_PRIVACY_POLICY")}
              </a>
            </li>
          )}
          {showModal && showComplianceOptOut && !needsComplianceAgreement && (
            <li>
              <button
                type="button"
                className="csw-menu-link csw-btn-link outline"
                ref={optOutFocusRef}
                onClick={toggleShowComplianceOptOutChoices}
                aria-expanded={showComplianceOptOutChoices}
                aria-controls={`${id}-opt-out-menu`}
              >
                {i18n.t("COMPLIANCE_OPT_OUT")}
              </button>
            </li>
          )}
          {showModal && (
            <li
              id={`${id}-opt-out-menu`}
              hidden={!showComplianceOptOutChoices}
              aria-hidden={!showComplianceOptOutChoices}
              className={showComplianceOptOutChoices ? "show" : "hide"}
            >
              {showComplianceOptOutChoices && (
                <section className="csw-menu-compliance outline" ref={optOutChoiceFocusRef} tabIndex="0">
                  <div className="csw-menu-compliance-message" aria-level="2" role="heading">
                    {i18n.t("COMPLIANCE_OPT_OUT_MESSAGE")}
                  </div>
                  <div className="csw-btns csw-stretch">
                    <button type="button" className="csw-btn csw-btn-sm outline" onClick={onOptOutCancel}>
                      {i18n.t("BUTTON_OPT_OUT_NO")}
                    </button>
                    <button type="button" className="csw-btn csw-btn-sm outline" onClick={onOptOut}>
                      {i18n.t("BUTTON_OPT_OUT_YES")}
                    </button>
                  </div>
                </section>
              )}
            </li>
          )}
          {isNotFramed && shareableLink && (
            <li>
              <ShareExpandable shareBtnText={i18n.t("SHARE")} />
            </li>
          )}
        </ul>
      </div>

      <footer
        className={classNames("csw-menu-modal-footer", {
          "csw-flex-end": isNullOrUndefOrEmpty(complianceUrl),
        })}
      >
        {!isNullOrUndefOrEmpty(complianceUrl) && <Compliance complianceUrl={complianceUrl} />}
        <Callout publisherUrl={publisherUrl} targetId={targetId} />
        <button
          type="button"
          className="csw-menu-btn csw-modal-footer-btn csw-btn csw-btn-icon outline sr-only sr-only-focusable"
          ref={closeButtonBottomRef}
          onKeyDown={(e) => onTabKey(e, modalFocusRef)}
          onClick={onClose}
          aria-label={i18n.t("MENU_CLOSE")}
          title={i18n.t("MENU_CLOSE")}
        >
          <IconClose />
          <span className="sr-only">{i18n.t("MENU_CLOSE")}</span>
        </button>
      </footer>
    </Modal>
  );
};

MenuModal.propTypes /* remove-proptypes */ = {
  id: PropTypes.string.isRequired,
  hostname: PropTypes.string.isRequired,
  shareableLink: PropTypes.object,
  backupAlias: PropTypes.string.isRequired,
  targetId: PropTypes.string.isRequired,
  complianceUrl: PropTypes.string,
  partnerName: PropTypes.string,
  privacyPolicyUrl: PropTypes.string,
  showModal: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  compliance: PropTypes.object,
  complianceState: PropTypes.string,
  onComplianceAgree: PropTypes.func.isRequired,
  theme: PropTypes.string,
  closeFocusRef: PropTypes.object.isRequired,
  focusable: PropTypes.bool,
  jot: PropTypes.func,
};

export default connect("hostname,shareableLink,backupAlias", actions)(MenuModal);
